import _ from 'lodash';
import React from 'react';
import Helmet from 'react-helmet';
import cookie from 'cookie-dough';
import provideContext from 'fluxible-addons-react/provideContext';
import { handleHistory } from 'fluxible-router';
import { connectToStores } from 'fluxible-addons-react';
import config from '../../config/client';
import shallowCompare from '../../lib/shallowCompare';
import CustomerStore from '../../stores/CustomerStore';
import ErrorPage from '../ErrorPage';
import MarketingLayout from '../../layouts/MarketingLayout';
import StoreLayout from '../../layouts/StoreLayout';
import Store from '../../models/Store';
import { RouteTypes } from '../../lib/BanquetRouteManager/lib/Routes';
import styles from './index.sass';
import appleTouchIcon60 from './images/apple-touch-icon-60.png';
import appleTouchIcon76 from './images/apple-touch-icon-76.png';
import appleTouchIcon120 from './images/apple-touch-icon-120.png';
import appleTouchIcon152 from './images/apple-touch-icon-152.png';
import favicon from './images/favicon.png';
import banquetOG from './images/banquet-og.jpg';

const env = (typeof window !== 'undefined' ? window.ENV : (process.env.NODE_ENV || 'development'));
const DefaultTitle = "Personalized Ecommerce for Wine, Beer, and Spirits — Banquet";
const DefaultDescription = `Effortlessly create a beautiful, mobile-friendly online store. We offer inventory management, CRM, local delivery, and more.`;

function applyDomainToImage(image) {
  if(image.match(/^http/)) {return image;}
  return config.CDN + image;
}

class Banquet extends React.Component {
  static contextTypes = {
    delectoryApi: React.PropTypes.object,
    getUserAgent: React.PropTypes.func,
    getExperimentSetting: React.PropTypes.func,
    setExperimentSetting: React.PropTypes.func,
    trackEvent: React.PropTypes.func,
    getCurrentStore: React.PropTypes.func,
    buildURLForRoute: React.PropTypes.func
  };

  static childContextTypes = {
    delectoryApi: React.PropTypes.object,
    getUserAgent: React.PropTypes.func,
    getExperimentSetting: React.PropTypes.func,
    setExperimentSetting: React.PropTypes.func,
    trackEvent: React.PropTypes.func,
    getCurrentStore: React.PropTypes.func,
    buildURLForRoute: React.PropTypes.func
  };

  getChildContext() {
    return {
      delectoryApi: this.props.context.delectoryApi,
      getUserAgent: this.props.context.getUserAgent,
      getExperimentSetting: this.props.context.getExperimentSetting,
      setExperimentSetting: this.props.context.setExperimentSetting,
      trackEvent: this.props.context.trackEvent,
      getCurrentStore: this.props.context.getCurrentStore,
      buildURLForRoute: this.props.context.buildURLForRoute
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.isNavigateComplete !== nextProps.isNavigateComplete ||
      this.props.currentNavigate !== nextProps.currentNavigate ||
      this.props.currentRoute !== nextProps.currentRoute ||
      this.props.context !== nextProps.context
    );
  }

  componentDidMount() {
    let { currentRoute } = this.props;
    if(currentRoute.segmentViewName) {
      let props = {value: currentRoute.segmentViewName};
      if(currentRoute.query) {
        if(currentRoute.query.ref) {
          props.referrer = currentRoute.query.ref;
        }
        if(currentRoute.query.cn) {
          props.campaign_name = currentRoute.query.cn;
        }
      }
      // Track initial page view. Subsequent client-side route changes are tracked below in `componentDidUpdate`
      this.props.context.trackEvent('view', props);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if(this.props.currentNavigate === prevProps.currentNavigate) {return;}
    let nav = this.props.currentNavigate;
    if(typeof window !== 'undefined') {
      // Scroll position
      let scroll = {};
      if(nav.type === 'popstate') {
        scroll = (window.history.state && window.history.state.scroll) || {};
      }
      window.scrollTo(scroll.x || 0, scroll.y || 0);
      // Analytics
      if(prevProps.isNavigateComplete && !this.props.isNavigateComplete && this.props.currentRoute && this.props.currentRoute.segmentViewName) {
        this.props.context.trackEvent('view', {value: this.props.currentRoute.segmentViewName});
      }
    }
  }

  getHelmetProps() {
    return {
      title: DefaultTitle,
      meta: [
        {name: 'viewport', content: 'width=device-width, user-scalable=no'},
        {name: 'description', content: DefaultDescription},
        {property: 'og:title', content: DefaultTitle},
        {property: 'og:description', content: DefaultDescription},
        {property: 'og:image', content: applyDomainToImage(banquetOG)},
        {property: 'og:type', content: 'website'},
        {property: 'fb:app_id', content: '128500384209429'},
        {name: 'twitter:site', content: '@delectable'},
        {name: 'twitter:creator', content: '@delectable'},
        {name: 'twitter:app:country', content: 'US'},
        {name: 'twitter:app:id:iphone', content: '1058119446'},
        {name: 'twitter:app:id:ipad', content: '1058119446'},
        {name: 'twitter:app:url:iphone', content: 'banquet://open'},
        {name: 'twitter:app:url:ipad', content: 'banquet://open'},
        {name: 'twitter:card', content: 'app'},
        {name: 'twitter:image', content: applyDomainToImage(banquetOG)},
        {name: 'twitter:description', content: DefaultDescription},
        {property: 'al:ios:url', content: 'banquet://open'},
        {name: 'apple-mobile-web-app-capable', content: 'yes'},
        {name: 'mobile-web-app-capable', content: 'yes'},
        {name: 'apple-mobile-web-app-title', content: 'Banquet'},
        {name: 'apple-mobile-web-app-status-bar-style', content: 'black'},
        {name: 'theme-color', content: '#f5f5f5'}
      ],
      link: [
        {href: applyDomainToImage(appleTouchIcon60), rel: 'apple-touch-icon-precomposed'},
        {href: applyDomainToImage(appleTouchIcon76), sizes: '76x76', rel: 'apple-touch-icon-precomposed'},
        {href: applyDomainToImage(appleTouchIcon120), sizes: '120x120', rel: 'apple-touch-icon-precomposed'},
        {href: applyDomainToImage(appleTouchIcon152), sizes: '152x152', rel: 'apple-touch-icon-precomposed'},
        {href: applyDomainToImage(favicon), rel: 'shortcut icon', type: 'image/vnd.microsoft.icon'},
        {href: applyDomainToImage(favicon), rel: 'shortcut icon', type: 'image/x-icon'}
      ]
    };
  }

  handleOnClickClearCookiesButton(e) {
    let c = cookie();
    c.remove('banquet_sk', {path: '/'});
    c.remove('banquet_st', {path: '/'});
    c.remove('banquet_x', {path: '/'});
    window.location.reload();
  }

  renderDevHelpers() {
    if(env !== 'production') {
      return (
        <button className={styles.clearCookiesButton} onClick={this.handleOnClickClearCookiesButton.bind(this)}>Clear Cookies</button>
      );
    }
  }

  render() {
    let { context, currentRoute } = this.props;
    let Handler, Layout;
    if(currentRoute) {
      if(context.getCurrentStore()) {
        Layout = StoreLayout;
      } else {
        Layout = MarketingLayout;
      }
      if(currentRoute.handler) {
        Handler = currentRoute.handler;
      }
    } else {
      Handler = ErrorPage;
    }

    let layoutProps = {
      ...this.props,
      options: currentRoute ? currentRoute.layoutOptions : null
    };

    if(currentRoute && (currentRoute.type === RouteTypes.StoreRequired || currentRoute.type === RouteTypes.StoreOptional)) {
      let auth = this.props.context.delectoryApi.getAuthCredentials();
      if(!auth.session_token) {
        Layout = null;
        Handler = ErrorPage;
        layoutProps.code = 401;
        layoutProps.message = 'You are not authorized to view this page.';
      }
    }

    if(!Layout) {
      return (
        <div>
          <Helmet {...this.getHelmetProps()} />
          <Handler {...layoutProps} />
          {this.renderDevHelpers()}
        </div>
      );
    }

    layoutProps.handler = Handler;

    return (
      <div className={styles.banquet}>
        <Helmet {...this.getHelmetProps()} />
        <Layout {...layoutProps} />
        {this.renderDevHelpers()}
      </div>
    );
  }
}

// and wrap that with context
Banquet = provideContext(Banquet);
// wrap with history handler
Banquet = handleHistory(Banquet, {enableScroll: false}); // Handled in RouteStore

export default Banquet;



// WEBPACK FOOTER //
// banquet/components/Banquet/index.js